import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Skeleton,
  IconButton,
  Dialog,
  alpha,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  Palette as PaletteIcon,
  ImageOutlined as ImageIcon,
  VisibilityOutlined as PreviewIcon,
} from "@mui/icons-material";
import {
  CLINIC_LOGO_MAX_BYTES,
  CLINIC_LOGO_ICON_MAX_BYTES,
  CLINIC_LOGO_MAX_WIDTH,
  CLINIC_LOGO_MAX_HEIGHT,
  CLINIC_LOGO_ICON_MAX_DIM,
  formatMaxSize,
} from "../../../constants/uploadLimits";
import { COLORS, TYPO, CARD_PAD, CARD_GAP, CARD_PAGE_PAD, dashboardSectionSx } from "../../../styles/uiConstants";
import { useAlert } from "../../../context/AlertProvider";
import { useHasPermission } from "../../../hooks/useHasPermission";
import useAuth from "../../../hooks/useAuth";
import { ActionButton, InfoRow, ThemeColorPicker } from "../../../components/atoms";
import { SectionCard } from "../../../components/molecules";
import { ClinicBrandPreview } from "../../../components/organisms";
import {
  useGetClinicAppearanceMutation,
  useUpdateClinicAppearanceMutation,
  useUploadClinicLogoMutation,
  useGetMyClinicAppearanceMutation,
  useUpdateMyClinicAppearanceMutation,
  useUploadMyClinicLogoMutation,
} from "../../../store/clinicApi";
import { URL } from "../../../store/utils";

// Görünüm — kartlı ayar sekmesi (design-principles §8.1): slate50 zemin,
// SectionCard + ikon + alt açıklama; her kart kendi kaydet butonuyla bağımsız kaydeder.
const cardSx = { ...dashboardSectionSx, display: "flex", flexDirection: "column" };

export default function AppearanceTab({ clinicId, token, palette, onSave, apiMode = "admin" }) {
  const isClinicMode = apiMode === "clinic";
  const { showSuccess, showError } = useAlert();
  // Klinik tarafında kullanıcı kendi paneli için tema/logo değiştiriyor; bu
  // yüzden başarılı kayıt sonrası `refreshMe()` ile auth.clinic'i tazeleyip
  // ClinicThemeProvider'ın anında yeni renge geçmesini sağlıyoruz.
  const { refreshMe } = useAuth();
  // Admin tarafında ayrı yetkiler var; klinik tarafında `clinic:appearance`
  // tek başına yeterli (route bazlı zaten guard ediyor).
  const canThemeAdmin = useHasPermission("admin-clinics:update-theme");
  const canLogoAdmin = useHasPermission("admin-clinics:update-logo");
  const canAppearanceClinic = useHasPermission("clinic:appearance");
  const canTheme = isClinicMode ? canAppearanceClinic : canThemeAdmin;
  const canLogo = isClinicMode ? canAppearanceClinic : canLogoAdmin;

  const [getAdmin, { data: adminData, isLoading: isAdminFetching }] = useGetClinicAppearanceMutation();
  const [getMine, { data: mineData, isLoading: isMineFetching }] = useGetMyClinicAppearanceMutation();
  // useCallback ile sabit referans — yoksa wrapper her render'da yeniden
  // oluşur, useEffect dep'i değişir, sonsuz fetch döngüsüne yol açar.
  const getClinicAppearance = useCallback(
    (args) => (isClinicMode ? getMine({ token: args?.token }) : getAdmin(args)),
    [isClinicMode, getMine, getAdmin]
  );
  const appearanceData = isClinicMode ? mineData : adminData;
  const isFetching = isClinicMode ? isMineFetching : isAdminFetching;

  const [updateAdmin, { isLoading: isUpdatingThemeAdmin }] = useUpdateClinicAppearanceMutation();
  const [updateMine, { isLoading: isUpdatingThemeMine }] = useUpdateMyClinicAppearanceMutation();
  const updateClinicAppearance = useCallback(
    (args) =>
      isClinicMode
        ? updateMine({
            token: args?.token,
            themeColor: args?.themeColor,
            logoUrl: args?.logoUrl,
            logoIconUrl: args?.logoIconUrl,
          })
        : updateAdmin(args),
    [isClinicMode, updateMine, updateAdmin]
  );
  const isUpdatingTheme = isClinicMode ? isUpdatingThemeMine : isUpdatingThemeAdmin;

  const [uploadAdmin, { isLoading: isUploadingAdmin }] = useUploadClinicLogoMutation();
  const [uploadMine, { isLoading: isUploadingMine }] = useUploadMyClinicLogoMutation();
  const uploadLogo = useCallback(
    (args) =>
      isClinicMode
        ? uploadMine({ token: args?.token, file: args?.file, logoType: args?.logoType })
        : uploadAdmin(args),
    [isClinicMode, uploadMine, uploadAdmin]
  );
  const isUploadingLogo = isClinicMode ? isUploadingMine : isUploadingAdmin;

  const [themeColor, setThemeColor] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoIconPreview, setLogoIconPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoIconFile, setLogoIconFile] = useState(null);
  const [logoError, setLogoError] = useState("");
  const [logoIconError, setLogoIconError] = useState("");
  const [clearLogo, setClearLogo] = useState(false);
  const [clearLogoIcon, setClearLogoIcon] = useState(false);
  const [previewDialogSrc, setPreviewDialogSrc] = useState("");

  useEffect(() => {
    if (token && clinicId) getClinicAppearance({ token, clinicId });
  }, [token, clinicId, getClinicAppearance]);

  const clinic = appearanceData?.data?.clinic;

  useEffect(() => {
    if (clinic) {
      setThemeColor(clinic.themeColor || null);
      setLogoPreview("");
      setLogoIconPreview("");
      setLogoFile(null);
      setLogoIconFile(null);
      setClearLogo(false);
      setClearLogoIcon(false);
    }
  }, [clinic]);

  const baseUrl = String(URL.url || "").replace(/\/+$/, "");
  const resolveLogoSrc = (raw) => {
    if (!raw) return "";
    const n = typeof raw === "string" ? raw.replace(/^\/+assets\//, "/assets/") : raw;
    return typeof n === "string" && n.startsWith("/assets/") ? `${baseUrl}${n}` : n;
  };

  const currentLogoSrc = logoPreview || resolveLogoSrc(clinic?.logoUrl);
  const currentIconSrc = logoIconPreview || resolveLogoSrc(clinic?.logoIconUrl);

  const validateAndSetFile = (file, opts, setPreview, setFile, setError, setClear) => {
    if (!file) return;
    setError("");
    const allowed = ["image/png", "image/jpeg", "image/webp"];
    if (!allowed.includes(file.type)) { setError("Sadece PNG, JPEG veya WebP yükleyebilirsiniz."); return; }
    if (file.size > opts.maxSize) { setError(`Boyut en fazla ${formatMaxSize(opts.maxSize)} olmalı.`); return; }
    // opts.maxDim: kare limit (icon). opts.maxWidth/maxHeight: dikdörtgen
    // limit (logo) — yatay geniş logolar için.
    const maxW = opts.maxWidth ?? opts.maxDim;
    const maxH = opts.maxHeight ?? opts.maxDim;
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const img = new Image();
      img.onload = () => {
        if (img.width > maxW || img.height > maxH) {
          setError(`Maks ${maxW}x${maxH} piksel. Yüklenen: ${img.width}x${img.height}`);
          return;
        }
        if (img.width < img.height) { setError(`Genişlik yükseklikten küçük olamaz.`); return; }
        setPreview(result);
        setFile(file);
        setClear(false);
      };
      img.onerror = () => setError("Görsel okunamadı.");
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const handleSaveTheme = async () => {
    try {
      const res = await updateClinicAppearance({ token, clinicId, themeColor }).unwrap();
      if (res?.success || res?.status === "success") {
        showSuccess("Tema rengi güncellendi");
        getClinicAppearance({ token, clinicId });
        if (isClinicMode) {
          // ClinicThemeProvider auth.clinic.themeColor'a bakar — refreshMe
          // ile auth tazelenince palette anında yeniden hesaplanır, sayfayı
          // yenilemeden tüm UI yeni renge geçer.
          refreshMe?.();
        }
        onSave?.();
      } else { showError(res?.message || "Güncelleme başarısız"); }
    } catch (err) { showError(err?.data?.message || "Hata oluştu"); }
  };

  const handleSaveLogo = async () => {
    try {
      const payload = { token, clinicId };
      if (clearLogo) payload.logoUrl = "";
      if (clearLogoIcon) payload.logoIconUrl = "";
      // Update appearance (clear flags)
      if (clearLogo || clearLogoIcon) {
        await updateClinicAppearance(payload).unwrap();
      }
      if (logoFile) {
        await uploadLogo({ token, clinicId, file: logoFile, logoType: "logo" }).unwrap();
      }
      if (logoIconFile) {
        await uploadLogo({ token, clinicId, file: logoIconFile, logoType: "icon" }).unwrap();
      }
      showSuccess("Logo güncellendi");
      setLogoFile(null);
      setLogoIconFile(null);
      getClinicAppearance({ token, clinicId });
      if (isClinicMode) {
        // Sidebar/header logo'ları auth.clinic.logoUrl/logoIconUrl üzerinden
        // okunur — refreshMe ile anında yeniden render olur.
        refreshMe?.();
      }
      onSave?.();
    } catch (err) { showError(err?.data?.message || "Logo güncellenirken hata oluştu"); }
  };

  // Yükleme: kart çerçeveleri ve başlıklar hemen çizilir; iskelet yalnız veri
  // alanlarında durur (design-principles §8.1).
  const loading = isFetching && !clinic;
  const hasLogoChanges = Boolean(logoFile || logoIconFile || clearLogo || clearLogoIcon);

  return (
    <Box
      sx={{
        ...CARD_PAGE_PAD,
        bgcolor: COLORS.slate50,
        minHeight: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: CARD_GAP,
      }}
    >
      {/* Üst satır: Tema rengi + Logolar */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1.4fr" }, gap: CARD_GAP, alignItems: "stretch" }}>
        {/* Tema Rengi */}
        <SectionCard
          title="Tema Rengi"
          subtitle="Panelde ve hasta uygulamasında kullanılan ana renk"
          icon={<PaletteIcon sx={{ fontSize: 18 }} />}
          sx={cardSx}
          action={canTheme && !loading ? (
            <ActionButton variant="save" compact palette={palette} loading={isUpdatingTheme} onClick={handleSaveTheme} />
          ) : null}
        >
          {loading ? (
            <Box sx={{ p: CARD_PAD, display: "flex", flexWrap: "wrap", gap: 1.25 }}>
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} variant="circular" width={36} height={36} />
              ))}
            </Box>
          ) : canTheme ? (
            <Box sx={{ p: CARD_PAD }}>
              <ThemeColorPicker value={themeColor} onChange={(hex) => setThemeColor(hex)} />
            </Box>
          ) : (
            <InfoRow label="Seçili renk" noBorder>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, pt: 0.25 }}>
                <Box sx={{ width: 22, height: 22, borderRadius: 1.5, bgcolor: themeColor || palette.primary, border: `1px solid ${COLORS.slate200}`, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.875rem", color: COLORS.slate700, fontVariantNumeric: "tabular-nums" }}>
                  {themeColor || "Varsayılan"}
                </Typography>
              </Box>
            </InfoRow>
          )}
        </SectionCard>

        {/* Klinik Logosu */}
        <SectionCard
          title="Klinik Logosu"
          subtitle="Ana logo ve dar alanlarda kullanılan ikon"
          icon={<ImageIcon sx={{ fontSize: 18 }} />}
          sx={cardSx}
          action={canLogo && !loading ? (
            <ActionButton variant="save" compact palette={palette} loading={isUploadingLogo} onClick={handleSaveLogo} disabled={!hasLogoChanges} />
          ) : null}
        >
          <Box sx={{ p: CARD_PAD, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: CARD_GAP, flex: 1 }}>
            {loading ? (
              [160, 120].map((h) => (
                <Box key={h} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="rounded" height={h} sx={{ borderRadius: 3 }} />
                </Box>
              ))
            ) : (
              <>
                {/* Ana Logo */}
                <LogoUploadBox
                  label="Ana Logo"
                  hint={`Maks ${CLINIC_LOGO_MAX_WIDTH}x${CLINIC_LOGO_MAX_HEIGHT}px (yatay), PNG/WebP/JPEG, ${formatMaxSize(CLINIC_LOGO_MAX_BYTES)}`}
                  currentSrc={clearLogo ? "" : currentLogoSrc}
                  error={logoError}
                  canEdit={canLogo}
                  onFileChange={(e) => validateAndSetFile(e.target.files?.[0], { maxSize: CLINIC_LOGO_MAX_BYTES, maxWidth: CLINIC_LOGO_MAX_WIDTH, maxHeight: CLINIC_LOGO_MAX_HEIGHT }, setLogoPreview, setLogoFile, setLogoError, setClearLogo)}
                  onClear={() => { setClearLogo(true); setLogoPreview(""); setLogoFile(null); }}
                  onPreview={(src) => setPreviewDialogSrc(src)}
                  palette={palette}
                />

                {/* Logo İkonu */}
                <LogoUploadBox
                  label="Logo İkonu"
                  hint={`Maks ${CLINIC_LOGO_ICON_MAX_DIM}x${CLINIC_LOGO_ICON_MAX_DIM}px, PNG/WebP/JPEG, ${formatMaxSize(CLINIC_LOGO_ICON_MAX_BYTES)}`}
                  currentSrc={clearLogoIcon ? "" : currentIconSrc}
                  error={logoIconError}
                  canEdit={canLogo}
                  onFileChange={(e) => validateAndSetFile(e.target.files?.[0], { maxSize: CLINIC_LOGO_ICON_MAX_BYTES, maxDim: CLINIC_LOGO_ICON_MAX_DIM }, setLogoIconPreview, setLogoIconFile, setLogoIconError, setClearLogoIcon)}
                  onClear={() => { setClearLogoIcon(true); setLogoIconPreview(""); setLogoIconFile(null); }}
                  onPreview={(src) => setPreviewDialogSrc(src)}
                  palette={palette}
                  small
                />
              </>
            )}
          </Box>
        </SectionCard>
      </Box>

      {/* Canlı önizleme: seçili renk + (yeni yüklenen ya da mevcut) logolarla web ve mobil */}
      <SectionCard
        title="Önizleme"
        subtitle="Kaydetmeden önce web panelinde ve hasta uygulamasında nasıl görüneceği"
        icon={<PreviewIcon sx={{ fontSize: 18 }} />}
        sx={cardSx}
      >
        <Box sx={{ p: CARD_PAD }}>
          {loading ? (
            <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
          ) : (
            <ClinicBrandPreview
              themeColor={themeColor || undefined}
              logoSrc={clearLogo ? "" : currentLogoSrc}
              iconSrc={clearLogoIcon ? "" : currentIconSrc}
              clinicName={clinic?.name}
            />
          )}
        </Box>
      </SectionCard>

      {/* Logo Preview Dialog */}
      <Dialog open={!!previewDialogSrc} onClose={() => setPreviewDialogSrc("")} maxWidth="sm">
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setPreviewDialogSrc("")}
            aria-label="Kapat"
            sx={{ position: "absolute", top: 8, right: 8, bgcolor: alpha(COLORS.slate800, 0.55), color: COLORS.white, "&:hover": { bgcolor: alpha(COLORS.slate800, 0.75) }, width: 28, height: 28 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <img src={previewDialogSrc} alt="Logo önizleme" style={{ display: "block", maxWidth: "100%", maxHeight: "80vh" }} />
        </Box>
      </Dialog>
    </Box>
  );
}

/* ── Logo Upload Box with Drag & Drop ── */
function LogoUploadBox({ label, hint, currentSrc, error, canEdit, onFileChange, onClear, onPreview, palette, small }) {
  const [isDragging, setIsDragging] = React.useState(false);
  const inputRef = React.useRef(null);

  const handleDragOver = (e) => { e.preventDefault(); if (canEdit) setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!canEdit) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) onFileChange({ target: { files: [file] } });
  };

  // Icon (small=true): 48×48 sabit kare. Logo (small=false): 72px yüksek,
  // genişlik mevcut alana göre 240px'e kadar genişler — yatay logo'lar
  // çarpıtılmadan görünür. Bu aynı zamanda kullanıcıya "max yüklenebilir
  // genişlik" referansını verir.
  const previewHeight = small ? 48 : 72;
  const previewWidth = small ? 48 : 240;

  return (
    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 40 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: TYPO.body, color: COLORS.slate800 }}>{label}</Typography>
          <Typography sx={{ color: COLORS.slate500, fontSize: TYPO.caption }}>{hint}</Typography>
        </Box>
        {canEdit && currentSrc && (
          <ActionButton
            variant="delete"
            compact
            tone="soft"
            label="Kaldır"
            onClick={onClear}
            icon={<DeleteIcon sx={{ fontSize: 14 }} />}
            sx={{ flexShrink: 0 }}
          />
        )}
      </Box>

      {/* Drop zone */}
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => canEdit && inputRef.current?.click()}
        sx={{
          border: `2px dashed ${isDragging ? palette.primary : COLORS.slate200}`,
          borderRadius: 3,
          bgcolor: isDragging ? alpha(palette.primary, 0.04) : COLORS.slate50,
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          minHeight: 160,
          cursor: canEdit ? "pointer" : "default",
          transition: "border-color 0.2s, background-color 0.2s",
          "&:hover": canEdit ? { borderColor: palette.primary, bgcolor: alpha(palette.primary, 0.02) } : {},
        }}
      >
        {currentSrc ? (
          <Box
            onClick={(e) => { e.stopPropagation(); onPreview(currentSrc); }}
            sx={{
              width: previewWidth, height: previewHeight, maxWidth: "100%", borderRadius: 2,
              border: `1px solid ${COLORS.slate200}`, bgcolor: COLORS.white,
              display: "flex", alignItems: "center", justifyContent: "center",
              overflow: "hidden", cursor: "pointer",
            }}
          >
            <img src={currentSrc} alt={label} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          </Box>
        ) : (
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(palette.primary, 0.08), display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ fontSize: 18, color: palette.primary, fontWeight: 500 }}>+</Typography>
          </Box>
        )}

        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 500, color: isDragging ? palette.primary : COLORS.slate600 }}>
            {isDragging ? "Bırakın" : currentSrc ? "Değiştirmek için tıklayın veya sürükleyin" : "Tıklayın veya sürükleyip bırakın"}
          </Typography>
          <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400, mt: 0.25 }}>
            PNG, WebP veya JPEG
          </Typography>
        </Box>

        {canEdit && (
          <input
            ref={inputRef}
            type="file"
            hidden
            accept="image/png,image/webp,image/jpeg"
            onChange={onFileChange}
          />
        )}
      </Box>

      {error && <Typography sx={{ fontSize: TYPO.label, color: COLORS.errorDark }}>{error}</Typography>}
    </Box>
  );
}
